/**
 * Нийлүүлэгчийн зээл — авсан, төлсөн, үлдсэн.
 *
 * Цуглуулагч Орост гутлыг зээлээр авдаг; төлбөр нь дараа, хэсэгчилж
 * хийгддэг. Энэ хуудас гурван асуултад хариулна:
 *   1. Хэнд хэдэн ₽ өртэй вэ
 *   2. Аль нь хугацаа хэтэрсэн бэ
 *   3. Тухайн аялалд хэзээ, хэдийг төлсөн бэ (түүх)
 *
 * ⚠️ Дүн бүр ₽-ЭЭР. Ханшаар ₮ болгож хөрвүүлэхгүй (дүрэм 4) — өр нь
 *    рублиэр үүссэн, рублиэр төлөгдөнө.
 * ⚠️ Төлбөр бүртгэх нь `credit.pay` — админ, нягтлан. Бусад нь харна.
 * ⚠️ «Төлсөн ₮» (2026-10-15) — бодитоор зарцуулсан төгрөг. Нягтлангийн
 *    «Ханшийн тэгшитгэл» үүнийг аяллын (цуглуулагчийн) ханштай тулгана.
 * ⚠️ «Авсан» нь ЗЭЭЛТЭЙ ГУТЛЫН нийлбэр (`items.is_credit`) — дашбоардын
 *    «Нийлүүлэгчийн зээл»-тэй ижил эх сурвалж (`GET /purchases/credit`).
 */

import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatDate, formatDateTime, formatMnt, formatQty, formatRate, formatRub, moneyInput } from '@boots/domain';
import { api, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Section } from '../components/section';
import { StatCard, StatGrid, StatPanel } from '../components/stat-card';
import {
  Button, Empty, ErrorNote, Field, Input, Modal, Spinner, Table, Td, Textarea, Th,
} from '../components/ui';

/** `GET /purchases/credit`-ийн мөр — зээлтэй гутал бүхий аялал. */
interface CreditTrip {
  id: string;
  code: string | null;
  purchasedAt: string;
  currency: string;
  supplier: { id: string; name: string };
  collector: { fullName: string };
  /** Зээлтэй гутлын тоо */
  items: number;
  /** Зээлээр авсан гутлын нийлбэр (₽). */
  takenAmount: string;
  paidAmount: string;
  leftAmount: string;
  creditDueDate: string | null;
}

interface Payment {
  id: string;
  amount: string;
  amountMnt: string | null;
  paidAt: string;
  note: string | null;
  voidedAt: string | null;
  recordedBy: { fullName: string };
  voidedBy: { fullName: string } | null;
}

interface PaymentsMeta {
  currency: string;
  takenAmount: string;
  paidAmount: string;
  leftAmount: string;
  dueDate: string | null;
}

const left = (t: CreditTrip) => Number(t.leftAmount);
const isOverdue = (t: CreditTrip) =>
  left(t) > 0 && !!t.creditDueDate && new Date(t.creditDueDate) < new Date();

export function CreditPage() {
  const { hasPerm } = useAuth();
  const [open, setOpen] = useState<CreditTrip | null>(null);

  const trips = useQuery({
    queryKey: ['credit-trips'],
    queryFn: () => api<Envelope<CreditTrip[]>>('/purchases/credit'),
    placeholderData: keepPreviousData,
  });

  if (trips.isPending) return <Spinner />;
  if (trips.error) return <ErrorNote>{(trips.error as Error).message}</ErrorNote>;

  // Бүрэн төлөгдөөгүй нь эхэнд; дараа нь төлөгдсөн
  const owing = trips.data.data.filter((t) => left(t) > 0.005);
  const settled = trips.data.data.filter((t) => left(t) <= 0.005);

  const totalLeft = owing.reduce((n, t) => n + left(t), 0);
  const overdue = owing.filter(isOverdue);
  const suppliers = new Set(owing.map((t) => t.supplier.id)).size;
  const nextDue = owing
    .filter((t) => t.creditDueDate)
    .sort((a, b) => (a.creditDueDate! < b.creditDueDate! ? -1 : 1))[0];

  return (
    <div className="flex flex-col gap-3">
      <StatPanel>
        <StatGrid>
          <StatCard
            label="Нийт өр"
            icon="coins"
            value={formatRub(totalLeft)}
            delta={{
              text: `${formatQty(suppliers)} нийлүүлэгч · ${formatQty(owing.length)} аялал`,
              good: totalLeft === 0 ? true : undefined,
            }}
            info={
              <>
                Нийлүүлэгчээс <b>зээлээр</b> авсан бөгөөд төлөгдөөгүй үлдэгдэл.
                Дүн нь <b>рублиэр</b> — ханшаар төгрөг болгож хөрвүүлэхгүй,
                учир нь өр рублиэр үүссэн, рублиэр төлөгдөнө.
              </>
            }
          />
          <StatCard
            label="Хугацаа хэтэрсэн"
            icon="percent"
            value={formatQty(overdue.length)}
            delta={{
              text: overdue.length
                ? `${formatRub(overdue.reduce((n, t) => n + left(t), 0))} төлөгдөөгүй`
                : 'Хэтэрсэн өр алга',
              good: overdue.length === 0,
            }}
            tone={overdue.length ? 'alert' : undefined}
          />
          <StatCard
            label="Хамгийн ойрын төлөлт"
            icon="receipt"
            value={nextDue?.creditDueDate ? formatDate(nextDue.creditDueDate) : '—'}
            delta={{
              text: nextDue ? `${nextDue.supplier.name} · ${formatRub(left(nextDue))}` : 'Хугацаа заагаагүй',
            }}
          />
        </StatGrid>
      </StatPanel>

      <Section title="Төлөгдөөгүй" note={`${formatQty(owing.length)} аялал`}>
        <TripTable rows={owing} onPick={setOpen} />
      </Section>

      {settled.length > 0 ? (
        <Section title="Төлөгдсөн" note={`${formatQty(settled.length)} аялал`}>
          <TripTable rows={settled} onPick={setOpen} />
        </Section>
      ) : null}

      {open ? (
        <PaymentsModal
          trip={open}
          canPay={hasPerm('credit.pay')}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </div>
  );
}

function TripTable({ rows, onPick }: { rows: CreditTrip[]; onPick: (t: CreditTrip) => void }) {
  if (rows.length === 0) return <Empty>Энд аялал алга</Empty>;

  return (
    <Table>
      <thead>
        <tr>
          <Th>Аялал</Th>
          <Th>Нийлүүлэгч</Th>
          <Th num>Авсан</Th>
          <Th num>Төлсөн</Th>
          <Th num>Үлдэгдэл</Th>
          <Th>Төлөх хугацаа</Th>
          <Th> </Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((t) => (
          <tr key={t.id} className="transition hover:bg-row-hover">
            <Td>
              <Link to={`/purchasing/${t.id}`} className="num hover:underline">
                {t.code ?? '—'}
              </Link>
              <span className="block text-meta text-ink-3">{formatDate(t.purchasedAt)}</span>
            </Td>
            <Td>{t.supplier.name}</Td>
            <Td num>
              {formatRub(t.takenAmount)}
              <span className="block text-meta text-ink-3">{formatQty(t.items)} ш</span>
            </Td>
            <Td num>{formatRub(t.paidAmount)}</Td>
            <Td num>
              <strong className={left(t) > 0 ? '' : 'text-ink-3'}>{formatRub(left(t))}</strong>
            </Td>
            <Td>
              {t.creditDueDate ? (
                <span className={isOverdue(t) ? 'text-alert-ink' : ''}>
                  {formatDate(t.creditDueDate)}
                  {isOverdue(t) ? ' · хэтэрсэн' : ''}
                </span>
              ) : (
                <span className="text-ink-3">заагаагүй</span>
              )}
            </Td>
            <td className="border-b border-hair/70 px-3 py-2 text-right">
              <button
                type="button"
                onClick={() => onPick(t)}
                className="inline-flex min-h-11 items-center rounded-full border border-hair
                  px-2.5 py-1 text-[11px] hover:bg-well sm:min-h-0"
              >
                Түүх
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

/** Нэг аяллын төлөлтийн түүх + шинэ төлбөр бүртгэх. */
function PaymentsModal({ trip, canPay, onClose }: {
  trip: CreditTrip;
  canPay: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [amount, setAmount] = useState('');
  const [amountMnt, setAmountMnt] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['payments', trip.id],
    queryFn: () =>
      api<Envelope<Payment[]> & { meta: PaymentsMeta }>(`/purchases/${trip.id}/payments`),
  });

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['payments', trip.id] });
    await qc.invalidateQueries({ queryKey: ['credit-trips'] });
    await qc.invalidateQueries({ queryKey: ['suppliers-report'] });
    await qc.invalidateQueries({ queryKey: ['dashboard'] });
  };

  const add = useMutation({
    mutationFn: () =>
      api(`/purchases/${trip.id}/payments`, {
        method: 'POST',
        body: {
          id: crypto.randomUUID(),
          amount: moneyInput(amount),
          ...(Number(moneyInput(amountMnt)) > 0 ? { amountMnt: moneyInput(amountMnt) } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        },
      }),
    onSuccess: async () => {
      setAmount('');
      setAmountMnt('');
      setNote('');
      setError(null);
      await refresh();
    },
    onError: (e) => setError((e as Error).message),
  });

  const voidOne = useMutation({
    mutationFn: (id: string) =>
      api(`/purchases/${trip.id}/payments/${id}/void`, { method: 'POST' }),
    onSuccess: refresh,
    onError: (e) => setError((e as Error).message),
  });

  const meta = list.data?.meta;
  const rows = list.data?.data ?? [];

  return (
    <Modal
      title={`${trip.code ?? 'Аялал'} · ${trip.supplier.name}`}
      subtitle={`${formatDate(trip.purchasedAt)} · ${trip.collector.fullName}`}
      onClose={onClose}
      footer={<Button variant="ghost" onClick={onClose}>Хаах</Button>}
    >
      <div className="flex flex-col gap-3">
        {meta ? (
          <dl className="grid grid-cols-3 gap-3 rounded-card border border-hair px-3.5 py-3 text-[13px]">
            <div>
              <dt className="text-[11.5px] text-ink-3">Авсан</dt>
              <dd className="num">{formatRub(meta.takenAmount)}</dd>
            </div>
            <div>
              <dt className="text-[11.5px] text-ink-3">Төлсөн</dt>
              <dd className="num">{formatRub(meta.paidAmount)}</dd>
            </div>
            <div>
              <dt className="text-[11.5px] text-ink-3">Үлдэгдэл</dt>
              <dd className="num font-semibold">{formatRub(meta.leftAmount)}</dd>
            </div>
          </dl>
        ) : null}

        {error ? <ErrorNote>{error}</ErrorNote> : null}

        {canPay && meta && Number(meta.leftAmount) > 0 ? (
          <div className="flex flex-col gap-2 rounded-card border border-hair px-3.5 py-3">
            <Field label={`Төлсөн дүн (${trip.currency})`}>
              <Input
                value={amount}
                onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))}
                inputMode="decimal"
                className="num"
                placeholder={meta.leftAmount}
              />
            </Field>
            <Field label="Төлсөн ₮ (бодитоор зарцуулсан)">
              <Input
                value={amountMnt}
                onChange={(e) => setAmountMnt(e.target.value.replace(/D/g, ''))}
                inputMode="numeric"
                className="num"
                placeholder="Ханшийн тэгшитгэлд хэрэгтэй"
              />
            </Field>
            {Number(amount) > 0 && Number(amountMnt) > 0 ? (
              <p className="text-meta text-ink-2">
                Төлсөн ханш <span className="num font-semibold">1 RUB = {formatRate(Number(amountMnt) / Number(amount))}₮</span>
              </p>
            ) : null}
            <Field label="Тайлбар">
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
            </Field>
            <Button
              onClick={() => add.mutate()}
              disabled={!amount || Number(amount) <= 0 || add.isPending}
            >
              {add.isPending ? 'Бүртгэж байна…' : 'Төлбөр бүртгэх'}
            </Button>
            <p className="text-[11.5px] text-ink-3">
              ⚠️ Бүртгэсэн төлбөр устгагдахгүй — алдаатай бол цуцална. Авснаас
              илүү дүн бүртгэх боломжгүй.
            </p>
          </div>
        ) : null}

        {list.isPending ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <Empty>Төлбөр бүртгэгдээгүй</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Хэзээ</Th>
                <Th num>Дүн</Th>
                <Th>Бүртгэсэн</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className={p.voidedAt ? 'text-ink-3 line-through' : undefined}>
                  <Td num>{formatDateTime(p.paidAt)}</Td>
                  <Td num>
                    {formatRub(p.amount)}
                    {p.amountMnt ? <span className="block text-meta text-ink-2">{formatMnt(p.amountMnt)}</span> : null}
                  </Td>
                  <Td>
                    {p.recordedBy.fullName}
                    {p.note ? <span className="block text-meta text-ink-2">{p.note}</span> : null}
                  </Td>
                  <Td>
                    {canPay && !p.voidedAt ? (
                      <button
                        type="button"
                        onClick={() => voidOne.mutate(p.id)}
                        disabled={voidOne.isPending}
                        className="text-alert-ink hover:underline"
                      >
                        Цуцлах
                      </button>
                    ) : null}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    </Modal>
  );
}
